import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import { createCustomMarkerElement, getBounds, IpPoint } from "../MapModel";
import { createPointClusterIndex, MapPointFeature, POINT_MARKER_SIZE } from "../MapPointClusters";
import { updateClusterLayers } from "../utils/clusterLayers";
import { bufferPointBounds, containsPointBounds } from "services/map/MapPointViewport";
import type { PointBounds } from "services/map/MapPointQuery";

const EMPTY_POINTS: IpPoint[] = [];
const clusterControlSize = (count: number) => count >= 750 ? 80 : count >= 100 ? 60 : 44;
interface UseMapMarkersProps {
  map: MapboxMap | null;
  points?: IpPoint[];
  categoryId: number;
  isMobile: boolean;
  onPinSelect: (point: IpPoint) => void;
}
const featureKey = (feature: MapPointFeature, zoom: number): string => feature.properties.cluster
  ? `cluster-${zoom}-${feature.geometry.coordinates.join(":")}` : `point-${feature.properties.pointId}`;

export const useMapMarkers = ({ map, points = EMPTY_POINTS, categoryId, isMobile, onPinSelect }: UseMapMarkersProps) => {
  const { t } = useTranslation();
  const markers = useRef(new Map<string, mapboxgl.Marker>());
  const markerCategory = useRef<number | null>(null);
  const pointDetails = useRef(new Map<string, IpPoint>());
  const visibleFeatures = useRef(new Map<string, MapPointFeature>());
  const onPinSelectRef = useRef(onPinSelect);
  const index = useMemo(() => createPointClusterIndex(points, isMobile, categoryId), [points, isMobile, categoryId]);
  const currentIndex = useRef(index);
  const [camera, setCamera] = useState<{ area: PointBounds; zoom: number } | null>(null);
  const [rendered, setRendered] = useState<{ points: IpPoint[]; categoryId: number; mobile: boolean } | null>(null);
  const [renderError, setRenderError] = useState(false);
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => { onPinSelectRef.current = onPinSelect; }, [onPinSelect]);
  useEffect(() => { setRendered(null); }, [map]);

  useEffect(() => {
    if (!map) return;
    let area: PointBounds | null = null;
    let zoom = -1;
    let frame: number | null = null;
    const updateCamera = () => {
      frame = null;
      const viewport = getBounds(map);
      const nextZoom = Math.floor(map.getZoom());
      if (area && containsPointBounds(area, viewport) && nextZoom === zoom) return;
      // Draw beyond the screen, so nearby pins are present before panning to them.
      area = bufferPointBounds(viewport, 0.5);
      zoom = nextZoom;
      setCamera({ area, zoom });
    };
    const scheduleCamera = () => { if (frame === null) frame = requestAnimationFrame(updateCamera); };
    updateCamera();
    map.on("move", scheduleCamera);
    return () => {
      map.off("move", scheduleCamera);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !camera) return;
    setRenderError(false);
    currentIndex.current = index;
    pointDetails.current = new Map(points.map((point) => [String(point.id), point]));
    if (markerCategory.current !== categoryId) {
      markers.current.forEach((marker) => marker.remove());
      markers.current.clear();
      markerCategory.current = categoryId;
    }
    const features: MapPointFeature[] = index.getClusters(camera.area, camera.zoom);
    const keyFor = (feature: MapPointFeature) => featureKey(feature, camera.zoom);
    visibleFeatures.current = new Map(features.map((feature) => [keyFor(feature), feature]));
    const nextKeys = new Set(features.map(keyFor));
    let cancelled = false;
    let frame: number | null = null;
    let next = 0;
    const chunkSize = isMobile ? 18 : 32;
    const failRender = () => { if (!cancelled) setRenderError(true); };
    const renderChunk = () => {
      if (cancelled) return;
      try {
        const end = Math.min(next + chunkSize, features.length);
        for (; next < end; next += 1) {
          const feature = features[next];
          const key = keyFor(feature);
          const existing = markers.current.get(key);
          const coordinates = feature.geometry.coordinates as [number, number];
          if (existing) {
            const position = existing.getLngLat();
            if (position.lng !== coordinates[0] || position.lat !== coordinates[1]) existing.setLngLat(coordinates);
            const element = existing.getElement();
            if (feature.properties.cluster) {
              element.setAttribute("aria-label", t("map.clusterLabel", { count: feature.properties.point_count }));
              element.style.width = element.style.height = `${clusterControlSize(feature.properties.point_count ?? 0)}px`;
            } else {
              const point = pointDetails.current.get(feature.properties.pointId);
              if (point) {
                element.style.backgroundImage = `url(${point.icon})`;
                element.setAttribute("aria-label", point.titlu || point.title || point.adresa || "Punct pe hartă");
              }
            }
            continue;
          }
          let element: HTMLElement;
          if (feature.properties.cluster) {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "map-cluster-control";
            const count = feature.properties.point_count ?? 0;
            button.style.width = button.style.height = `${clusterControlSize(count)}px`;
            button.setAttribute("aria-label", t("map.clusterLabel", { count }));
            button.addEventListener("click", (event) => {
              event.preventDefault();
              event.stopPropagation();
              const selected = visibleFeatures.current.get(key);
              if (!selected?.properties.cluster) return;
              map.easeTo({
                center: selected.geometry.coordinates as [number, number],
                zoom: Math.min(currentIndex.current.getClusterExpansionZoom(selected.properties.cluster_id), map.getMaxZoom()),
                duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 500,
              });
            });
            element = button;
          } else {
            const id = feature.properties.pointId;
            const point = pointDetails.current.get(id);
            if (!point?.icon) continue;
            element = createCustomMarkerElement(point.icon, POINT_MARKER_SIZE,
              () => onPinSelectRef.current(pointDetails.current.get(id) ?? point), point);
          }
          markers.current.set(key, new mapboxgl.Marker({ element }).setLngLat(coordinates).addTo(map));
        }
        if (next < features.length) frame = requestAnimationFrame(renderChunk);
        else {
          // Preserve the old cohort until the new one is ready; reuse unchanged DOM.
          markers.current.forEach((marker, key) => {
            if (!nextKeys.has(key)) { marker.remove(); markers.current.delete(key); }
          });
          updateClusterLayers(map, features.filter((feature) => feature.properties.cluster));
          setRendered((previous) => previous?.points === points && previous.categoryId === categoryId && previous.mobile === isMobile
            ? previous : { points, categoryId, mobile: isMobile });
        }
      } catch { failRender(); }
    };
    renderChunk();
    return () => { cancelled = true; if (frame !== null) cancelAnimationFrame(frame); };
  }, [map, points, camera, index, isMobile, categoryId, revision, t]);

  useEffect(() => {
    const currentMarkers = markers.current;
    return () => { currentMarkers.forEach((marker) => marker.remove()); currentMarkers.clear(); markerCategory.current = null; };
  }, [map]);
  return {
    isRendering: Boolean(map && points.length > 0 && !renderError &&
      (rendered?.points !== points || rendered.categoryId !== categoryId || rendered.mobile !== isMobile)),
    renderError,
    retry,
  };
};
